import React, { useState, useEffect, memo } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useTranslation } from 'react-i18next';
import CreatableSelect from 'react-select/creatable';
import { makeStyles } from '@material-ui/core/styles';
import Pagination from '@material-ui/lab/Pagination';
import PeriodSelector from './components/PeriodSelector';
import ListHistoryActions from './components/ListHistoryActions';
import { CompanyActions } from '../../../../../store/actions/companies';
import InputDropdown from '../../../../../bergmannsoft/components/Widgets/Forms/InputDropdown';
import './index.css';

const ACTION_OPTIONS = [
  { _id: 'INSERT', name: 'INSERÇÃO' },
  { _id: 'UPDATE', name: 'ATUALIZAÇÃO' },
  { _id: 'DELETE', name: 'REMOÇÃO' },
  { _id: 'ANANONYMIZE', name: 'ANONIMIZAÇÃO' },
];

const paginationStyle = makeStyles((theme) => ({
  root: {
    '& > *': {
      marginTop: theme.spacing(2),
    },
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
    color: '#515f75',
  },
}));

const getCorrectUtc = (completeUtc) => {
  const defaultValue = '-03:00';

  try {
    const temp = completeUtc.split(')')[0].split('GMT')[1].split('');

    if (temp.length === 5) {
      temp.splice(1, 0, '0');
    }

    if (temp.length !== 6) {
      return defaultValue;
    }

    return temp.join('');
  } catch (error) {
    return defaultValue;
  }
};

const AuditDashboard = ({
  companyId,
  historyActions,
  getHistoryActions,
  getCompany,
  timezone,
}) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [customPeriod, setCustomPeriod] = useState(0);
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);
  const { i18n } = useTranslation();
  const classes = paginationStyle();

  useEffect(() => {
    if (companyId) {
      getHistoryActions({ companyId });
      getCompany({ companyId });
    }
  }, []);

  const changePeriod = (newStartDate, newEndDate, customPeriodOption) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setCustomPeriod(customPeriodOption);
  };

  const getPage = (event, page) => {
    const traitedUsers =
      users && users.length > 0
        ? users.map((user) => user.value).join(',')
        : '';
    const traitedActions =
      actions && actions.length > 0 ? actions.join(',') : '';

    getHistoryActions({
      companyId,
      page,
      startDate: startDate
        ? moment(startDate)
            .utcOffset(getCorrectUtc(timezone.fuso), true)
            .toISOString()
        : '',
      endDate: endDate
        ? moment(endDate)
            .utcOffset(getCorrectUtc(timezone.fuso), true)
            .toISOString()
        : '',
      users: traitedUsers,
      actions: traitedActions,
    });

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const onClickFilter = () => {
    const traitedUsers =
      users && users.length > 0
        ? users.map((user) => user.value).join(',')
        : '';
    const traitedActions =
      actions && actions.length > 0 ? actions.join(',') : '';

    getHistoryActions({
      companyId,
      startDate: startDate
        ? moment(startDate)
            .utcOffset(getCorrectUtc(timezone.fuso), true)
            .toISOString()
        : '',
      endDate: endDate
        ? moment(endDate)
            .utcOffset(getCorrectUtc(timezone.fuso), true)
            .toISOString()
        : '',
      users: traitedUsers,
      actions: traitedActions,
    });
  };

  return (
    <div className="audit-page-content">
      <div className="audit-page-filters">
        <div className="audit-page-filters-options">
          <div className="container-period-selector">
            <span>Filtrar Período:</span>
            <PeriodSelector
              startDate={startDate}
              endDate={endDate}
              changePeriod={changePeriod}
              customPeriodOption={customPeriod}
            />
          </div>
          <div className="audit-page-more-filters">
            <div className="audit-page-by-action">
              <span>Filtrar Ações:</span>
              <InputDropdown
                className="settings-content-modal-input"
                placeholder={i18n.t('Selecione as ações')}
                dropdownItemsList={ACTION_OPTIONS}
                dropdownItemsChecked={actions}
                onChange={(items) => setActions(items)}
                useCheckbox
                styleWidth={200}
              />
            </div>
            <div className="audit-page-by-user">
              <span>Filtrar Usuário:</span>
              <CreatableSelect
                value={users}
                placeholder="Selecione"
                onChange={(newUsers) => setUsers(newUsers)}
                className="audit-react-select"
                classNamePrefix="audit-react-select"
                isMulti
                formatCreateLabel={(inputValueOnCreate) =>
                  `Criar "${inputValueOnCreate}"`
                }
                noOptionsMessage={() => null}
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 5,
                  colors: {
                    ...theme.colors,
                    primary: '#5e6876',
                    primary25: '#D7DADB',
                    danger: 'white',
                    dangerLight: '#5e6876',
                    neutral80: '#515f75',
                    neutral10: '#DEE0E4',
                  },
                })}
              />
            </div>
          </div>
        </div>
        <button
          className="btn-filter-filter"
          onClick={onClickFilter}
          type="button"
        >
          Filtrar
        </button>
      </div>

      <ListHistoryActions
        historyActions={historyActions.docs}
        fusoName={timezone && timezone.fusoName}
      />

      <div className={classes.root}>
        <Pagination
          count={historyActions.totalPages}
          page={historyActions.page}
          size="large"
          variant="outlined"
          onChange={getPage}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    companyId: state.users.signedUser?.selectedCompany?._id,
    timezone: state.companies.company && state.companies.company.timezone,
    historyActions: state.companies.historyActions,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...CompanyActions }, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(memo(AuditDashboard));
